export type SupportedLocale = "zh-CN" | "en-US" | "de-DE";

export type LocaleOption = {
  value: SupportedLocale;
  label: string;
  nativeLabel: string;
};

export const DEFAULT_LOCALE: SupportedLocale = "zh-CN";
export const DEFAULT_CONTENT_LOCALE: SupportedLocale = "zh-CN";

export const SUPPORTED_UI_LOCALES: SupportedLocale[] = ["zh-CN", "en-US", "de-DE"];
export const SUPPORTED_CONTENT_LOCALES: SupportedLocale[] = ["zh-CN", "en-US", "de-DE"];

const LOCALE_OPTIONS: LocaleOption[] = [
  { value: "zh-CN", label: "Chinese (Simplified)", nativeLabel: "简体中文" },
  { value: "en-US", label: "English (US)", nativeLabel: "English (US)" },
  { value: "de-DE", label: "German (Germany)", nativeLabel: "Deutsch" },
];

const LOCALE_DISPLAY_NAMES: Record<SupportedLocale, Record<SupportedLocale, string>> = {
  "zh-CN": { "zh-CN": "简体中文", "en-US": "Simplified Chinese", "de-DE": "Vereinfachtes Chinesisch" },
  "en-US": { "zh-CN": "英语", "en-US": "English", "de-DE": "Englisch" },
  "de-DE": { "zh-CN": "德语", "en-US": "German", "de-DE": "Deutsch" },
};

export const UI_LOCALE_OPTIONS: LocaleOption[] = LOCALE_OPTIONS.filter((option) => SUPPORTED_UI_LOCALES.includes(option.value));
export const CONTENT_LOCALE_OPTIONS: LocaleOption[] = LOCALE_OPTIONS.filter((option) => SUPPORTED_CONTENT_LOCALES.includes(option.value));

const LOCALE_ALIASES: Record<string, SupportedLocale> = {
  zh: "zh-CN",
  "zh-cn": "zh-CN",
  "zh-hans": "zh-CN",
  "zh-hans-cn": "zh-CN",
  en: "en-US",
  "en-us": "en-US",
  de: "de-DE",
  "de-de": "de-DE",
};

export function canonicalLocale(value: unknown): SupportedLocale | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().replace("_", "-");
  if (!normalized) return null;
  const alias = LOCALE_ALIASES[normalized.toLowerCase()];
  if (alias) return alias;
  return SUPPORTED_UI_LOCALES.includes(normalized as SupportedLocale) || SUPPORTED_CONTENT_LOCALES.includes(normalized as SupportedLocale)
    ? (normalized as SupportedLocale)
    : null;
}

export function resolvePreferredLocale(candidates: unknown[], fallback: SupportedLocale = DEFAULT_LOCALE): SupportedLocale {
  for (const candidate of candidates) {
    const locale = canonicalLocale(candidate);
    if (locale) return locale;
  }
  return fallback;
}

export function localeDisplayName(locale: string | null | undefined, uiLocale?: string | null): string {
  const canonical = canonicalLocale(locale);
  if (!canonical) return String(locale ?? "");
  const canonicalUiLocale = canonicalLocale(uiLocale);
  return canonicalUiLocale
    ? LOCALE_DISPLAY_NAMES[canonical][canonicalUiLocale]
    : LOCALE_OPTIONS.find((option) => option.value === canonical)?.nativeLabel ?? canonical;
}

/**
 * 成员在某租户下实际生效的生成内容语言。
 *
 * 后端已在 session 的每个 tenant 上算好 effectiveContentLocale（个人解耦 → 个人界面
 * 语言 → 租户内容默认）。这里回退到 defaultContentLocale 只是为了前端先于后端上线时
 * 不至于退化成中文，后端部署后这条回退就不会再命中。
 */
export function tenantContentLocale(
  tenant: { effectiveContentLocale?: string; defaultContentLocale?: string } | null | undefined,
): SupportedLocale {
  return resolvePreferredLocale([tenant?.effectiveContentLocale, tenant?.defaultContentLocale], DEFAULT_CONTENT_LOCALE);
}

/** 从会话里取某租户的生效生成语言。移动端各页只有 session + tenantSlug，走这个入口。 */
export function sessionContentLocale(
  session:
    | { tenants?: { slug: string; effectiveContentLocale?: string; defaultContentLocale?: string }[] }
    | null
    | undefined,
  tenantSlug: string,
): SupportedLocale {
  return tenantContentLocale(session?.tenants?.find((tenant) => tenant.slug === tenantSlug));
}
